import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Shield, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Check, 
  X,
  Activity,
  Clock,
  Key
} from 'lucide-react';

export default function AdminManagement({ currentUser }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'admin.list',
          data: { user: currentUser }
        })
      });
      const result = await response.json();
      if (result.success) {
        setAdmins(result.data);
      }
    } catch (error) {
      console.error('Failed to load admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePermissions = async (adminId, permissions) => {
    try {
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'admin.updatePermissions',
          data: {
            adminId,
            permissions,
            user: currentUser
          }
        })
      });
      const result = await response.json();
      if (result.success) {
        loadAdmins();
      }
    } catch (error) {
      console.error('Failed to update permissions:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading admins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Admin Management</h2>
          <p className="text-slate-400">Manage admin users and their permissions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all shadow-lg"
        >
          <Plus className="w-5 h-5" />
          <span>Add Admin</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Total Admins</p>
              <p className="text-3xl font-bold text-white">{admins.length}</p>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-lg">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Active Today</p>
              <p className="text-3xl font-bold text-white">
                {admins.filter(a => {
                  const lastLogin = new Date(a.last_login);
                  const today = new Date();
                  return lastLogin.toDateString() === today.toDateString();
                }).length}
              </p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-lg">
              <Activity className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-1">Super Admins</p>
              <p className="text-3xl font-bold text-white">
                {admins.filter(a => a.admin_level === 'super_admin').length}
              </p>
            </div>
            <div className="bg-purple-500/20 p-3 rounded-lg">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Admin List */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Admin
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Level
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {admins.map((admin) => (
              <tr key={admin.id} className="hover:bg-slate-700/30 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {admin.display_name || admin.email}
                    </p>
                    <p className="text-xs text-slate-400">{admin.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    admin.admin_level === 'super_admin'
                      ? 'bg-purple-500/20 text-purple-300'
                      : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {admin.admin_level === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {admin.can_access_db_analytics && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs">
                        DB Analytics
                      </span>
                    )}
                    {admin.can_access_ai_insights && (
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                        AI Insights
                      </span>
                    )}
                    {admin.can_access_external_db && (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                        External DB
                      </span>
                    )}
                    {admin.can_manage_users && (
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded text-xs">
                        Manage Users
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>
                      {admin.last_login 
                        ? new Date(admin.last_login).toLocaleDateString()
                        : 'Never'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {admin.admin_level !== 'super_admin' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedAdmin(admin)}
                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Permissions Modal */}
      {selectedAdmin && (
        <PermissionsModal
          admin={selectedAdmin}
          onClose={() => setSelectedAdmin(null)}
          onSave={(permissions) => {
            updatePermissions(selectedAdmin.id, permissions);
            setSelectedAdmin(null);
          }}
        />
      )}
    </div>
  );
}

function PermissionsModal({ admin, onClose, onSave }) {
  const [permissions, setPermissions] = useState({
    can_access_db_analytics: admin.can_access_db_analytics || false,
    can_access_ai_insights: admin.can_access_ai_insights || false,
    can_access_external_db: admin.can_access_external_db || false,
    can_manage_users: admin.can_manage_users || false,
    can_view_logs: admin.can_view_logs || false,
    can_export_data: admin.can_export_data || false,
    can_read_athletes: admin.can_read_athletes || false,
    can_write_athletes: admin.can_write_athletes || false,
  });

  const permissionGroups = [
    {
      title: 'Feature Access',
      permissions: [
        { key: 'can_access_db_analytics', label: 'DB Analytics', icon: Activity },
        { key: 'can_access_ai_insights', label: 'AI Insights', icon: Shield },
        { key: 'can_access_external_db', label: 'External DB', icon: Settings },
      ]
    },
    {
      title: 'User Management',
      permissions: [
        { key: 'can_manage_users', label: 'Manage Users', icon: Users },
        { key: 'can_view_logs', label: 'View Logs', icon: Clock },
        { key: 'can_export_data', label: 'Export Data', icon: Activity },
      ]
    },
    {
      title: 'Data Access',
      permissions: [
        { key: 'can_read_athletes', label: 'Read Athletes', icon: Users },
        { key: 'can_write_athletes', label: 'Write Athletes', icon: Edit },
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Edit Permissions</h3>
            <p className="text-sm text-slate-400">{admin.email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {permissionGroups.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-semibold text-slate-300 mb-3">{group.title}</h4>
              <div className="space-y-2">
                {group.permissions.map((perm) => {
                  const Icon = perm.icon;
                  return (
                    <label
                      key={perm.key}
                      className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="w-5 h-5 text-slate-400" />
                        <span className="text-sm text-white">{perm.label}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={permissions[perm.key]}
                        onChange={(e) => setPermissions({
                          ...permissions,
                          [perm.key]: e.target.checked
                        })}
                        className="w-5 h-5 rounded bg-slate-600 border-slate-500 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(permissions)}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
